/**
 * Privé Customer Brain — Intelligence Collector
 * Sends anonymous visitor behavior events to prive-customer-brain.fly.dev
 *
 * Tracks: session context (referrer, UTM, landing), product views, dwell time,
 *         searches, similar clicks, WhatsApp taps, image searches,
 *         customer identity linking.
 */

(function() {
  'use strict';

  var API = 'https://prive-customer-brain.fly.dev';
  var SESSION_FLAG = 'prive_brain_session_started';
  var MAX_RETRIES = 2;

  function _readToken() {
    var el = document.querySelector('meta[name="prive-intel-token"]');
    if (!el) return { ts: '', hmac: '', raw: '' };
    var raw = el.getAttribute('content') || '';
    var idx = raw.indexOf(':');
    if (idx === -1) return { ts: '', hmac: '', raw: '' };
    return { ts: raw.substring(0, idx), hmac: raw.substring(idx + 1), raw: raw };
  }

  var _auth = _readToken();

  // HMAC auth — read time-limited token injected by Liquid
  var _tsMeta = document.querySelector('meta[name="prive-ts"]');
  var _tokenMeta = document.querySelector('meta[name="prive-token"]');
  var AUTH_TS = _tsMeta ? _tsMeta.content : '';
  var AUTH_TOKEN = _tokenMeta ? _tokenMeta.content : '';

  var BrainIntel = {
    _queue: [],
    _flushTimer: null,
    _vid: null,
    _unloading: false,
    _rawFetch: null,
    _cartVidSyncing: false,
    _lastCartSnapshot: null,

    init: function() {
      try {
        this._vid = localStorage.getItem('prive_brain_vid');
        if (!this._vid) {
          this._vid = 'v_' + (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(36) + '_' + Math.random().toString(36).substring(2, 11));
          localStorage.setItem('prive_brain_vid', this._vid);
        }
      } catch (e) {
        // localStorage blocked (incognito / Safari ITP) — fall back to
        // sessionStorage so the VID at least survives within the tab.
        try {
          this._vid = sessionStorage.getItem('prive_brain_vid');
          if (!this._vid) {
            this._vid = 'v_' + (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(36) + '_' + Math.random().toString(36).substring(2, 11));
            sessionStorage.setItem('prive_brain_vid', this._vid);
          }
        } catch (_) {
          this._vid = 'v_anon_' + Math.random().toString(36).substring(2, 9);
        }
      }
      try { this._rawFetch = window.fetch ? window.fetch.bind(window) : null; } catch (_) { this._rawFetch = null; }
      this._maybeFireSessionStart();
      this._linkLoggedInCustomer();
      this._autoTrackProductView();
      this._autoTrackSearch();
      this._wrapFetchForCart();
      this._trackCartView();
      this._trackCheckoutStart();
      this._persistVidToCart('init');
      var self = this;
      window.addEventListener('pagehide', function() { self._unloading = true; self._flush(); });
    },

    _maybeFireSessionStart: function() {
      var already = false;
      try { already = sessionStorage.getItem(SESSION_FLAG) === '1'; } catch (e) {}
      if (already) return;

      var data = {};
      try { data.landing_path = window.location.pathname; } catch (e) {}
      try {
        var ref = document.referrer || '';
        if (ref) {
          try { var u = new URL(ref); data.referrer = u.origin + u.pathname; } catch (_) { data.referrer = null; }
        } else { data.referrer = null; }
      } catch (e) {}

      // UTM params (ad-source attribution)
      try {
        var params = new URLSearchParams(window.location.search);
        ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach(function(k) {
          var v = params.get(k);
          if (v) data[k] = v.slice(0, 120);
        });
      } catch (e) {}

      // Device / viewport — captured once, cheap signal for segmentation
      try {
        if (window.screen) {
          data.screen_w = window.screen.width;
          data.screen_h = window.screen.height;
        }
        data.viewport_w = window.innerWidth;
        data.viewport_h = window.innerHeight;
      } catch (e) {}

      try { data.language = navigator.language || null; } catch (e) {}
      try { data.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone; } catch (e) {}

      this._track('session_start', data);
      try { sessionStorage.setItem(SESSION_FLAG, '1'); } catch (e) {}
    },

    _linkLoggedInCustomer: function() {
      try {
        var meta = document.querySelector('meta[name="prive-cid"]');
        var cid = meta && meta.content;
        if (cid) this._maybeIdentify(String(cid));
      } catch (_) {}
    },

    _track: function(event, data) {
      this._queue.push({ e: event, d: data || {}, t: Date.now(), vid: this._vid });
      var self = this;
      if (this._queue.length >= 5) {
        this._flush();
      } else if (!this._flushTimer) {
        this._flushTimer = setTimeout(function() { self._flush(); }, 3000);
      }
    },

    _flush: function(retryCount) {
      if (this._flushTimer) { clearTimeout(this._flushTimer); this._flushTimer = null; }
      if (this._queue.length === 0) return;
      var batch = this._queue.splice(0);
      var payload = { events: batch, vid: this._vid, ts: AUTH_TS, token: AUTH_TOKEN };
      var body = JSON.stringify(payload);
      var url = API + '/api/intelligence';
      // Use sendBeacon only during page unload (no response inspection possible);
      // prefer fetch for normal flushes so we can detect and retry on 401.
      if (this._unloading && navigator.sendBeacon) {
        var blob = new Blob([body], { type: 'application/json' });
        navigator.sendBeacon(url, blob);
      } else {
        var self = this;
        var attempt = retryCount || 0;
        var headers = { 'Content-Type': 'application/json', 'X-Prive-TS': AUTH_TS, 'X-Prive-Token': AUTH_TOKEN };
        fetch(url, {
          method: 'POST',
          headers: headers,
          body: body,
          keepalive: true
        }).then(function(resp) {
          if (resp.status === 401 && attempt < MAX_RETRIES) {
            // Re-queue events so they aren't lost; retry on next flush cycle
            self._queue = batch.concat(self._queue);
            self._flushTimer = setTimeout(function() { self._flush(attempt + 1); }, 5000);
          }
        }).catch(function() {});
      }
    },

    _autoTrackProductView: function() {
      var meta = document.querySelector('meta[property="og:type"][content="product"]');
      if (!meta) return;
      var handle = window.location.pathname.split('/products/')[1];
      if (!handle) return;
      handle = handle.split('?')[0].split('#')[0];
      var data = { handle: handle };
      // Per-field extraction: JSON-LD for title/vendor, DOM for price (reflects
      // the currently selected variant), with cross-fallback for missing fields.
      var ld = this._productJsonLd();
      var titleEl = document.querySelector('.product-meta__title');
      var vendorEl = document.querySelector('.product-meta__vendor');
      var priceEl = document.querySelector('.price--large');
      // Title: JSON-LD first, DOM fallback
      if (ld && ld.name) { data.title = ld.name; }
      else if (titleEl) { data.title = titleEl.textContent.trim(); }
      // Vendor: JSON-LD first, DOM fallback
      if (ld && ld.brand && ld.brand.name) { data.vendor = ld.brand.name; }
      else if (vendorEl) { data.vendor = vendorEl.textContent.trim(); }
      // Price: DOM first (shows the selected variant's rendered price),
      // JSON-LD fallback (offers[0] may not match the active variant).
      if (priceEl) {
        var dp = parseFloat(priceEl.textContent.replace(/[^\d.,]/g, '').replace(',', ''));
        if (!isNaN(dp)) data.price = dp;
      }
      if (data.price == null && ld && ld.offers) {
        var offerPrice = Array.isArray(ld.offers) ? ld.offers[0] && ld.offers[0].price : ld.offers.price;
        if (offerPrice != null) { var lp = parseFloat(offerPrice); if (!isNaN(lp)) data.price = lp; }
      }
      this._track('product_view', data);
      var dwellData = { handle: handle };
      if (data.title) dwellData.title = data.title;
      if (data.vendor) dwellData.vendor = data.vendor;
      if (data.price != null) dwellData.price = data.price;
      // Dwell time tracking
      var self = this;
      var start = Date.now();
      var d30 = false, d60 = false;
      var iv = setInterval(function() {
        if (document.hidden) return;
        var s = Math.floor((Date.now() - start) / 1000);
        if (s >= 30 && !d30) { d30 = true; self._track('product_dwell', Object.assign({ seconds: 30 }, dwellData)); }
        if (s >= 60 && !d60) { d60 = true; self._track('product_dwell', Object.assign({ seconds: 60 }, dwellData)); clearInterval(iv); }
      }, 10000);
      window.addEventListener('pagehide', function() { clearInterval(iv); }, { once: true });
    },

    _autoTrackSearch: function() {
      var self = this;
      var last = '';
      var debounce = null;
      document.addEventListener('input', function(e) {
        if (!e.target || !e.target.matches || !e.target.matches('.predictive-search__input')) return;
        var q = e.target.value.trim();
        if (q.length >= 3 && q !== last) {
          last = q;
          clearTimeout(debounce);
          debounce = setTimeout(function() { self._track('search', { query: q }); }, 400);
        }
      }, true);
    },

    _cartSnapshotData: function(cart) {
      if (!cart || typeof cart !== 'object') return {};
      var data = {};
      if (cart.token) data.cart_token = String(cart.token);
      if (typeof cart.item_count !== 'undefined') data.cart_item_count = cart.item_count;
      if (typeof cart.total_price !== 'undefined') data.cart_total_price = cart.total_price;
      if (cart.currency) data.currency = cart.currency;
      try {
        var attrs = cart.attributes || {};
        if (attrs && attrs._prive_vid) data.cart_prive_vid = String(attrs._prive_vid);
      } catch (_) {}
      try {
        if (cart.items && cart.items.length) {
          data.cart_lines = cart.items.slice(0, 12).map(function(item) {
            var line = {};
            if (item.key) line.key = String(item.key);
            if (item.id) line.variant_id = String(item.id);
            if (item.variant_id) line.variant_id = String(item.variant_id);
            if (item.product_id) line.product_id = String(item.product_id);
            if (item.handle) line.handle = String(item.handle);
            if (item.url) line.url = String(item.url).split('?')[0];
            if (item.title) line.title = String(item.title).slice(0, 180);
            if (item.vendor) line.vendor = String(item.vendor).slice(0, 120);
            if (typeof item.quantity !== 'undefined') line.quantity = item.quantity;
            if (typeof item.price !== 'undefined') line.price = item.price;
            if (typeof item.final_price !== 'undefined') line.final_price = item.final_price;
            if (typeof item.line_price !== 'undefined') line.line_price = item.line_price;
            return line;
          });
        }
      } catch (_) {}
      return data;
    },

    _readCartSnapshot: function(keepalive) {
      if (!this._rawFetch) return Promise.resolve(null);
      var self = this;
      try {
        return this._rawFetch('/cart.js', {
          method: 'GET',
          headers: { 'Accept': 'application/json' },
          credentials: 'same-origin',
          keepalive: !!keepalive
        }).then(function(resp) {
          if (!resp || !resp.ok) return null;
          return resp.json();
        }).then(function(cart) {
          if (cart) self._lastCartSnapshot = cart;
          return cart || null;
        }).catch(function() { return null; });
      } catch (_) {
        return Promise.resolve(null);
      }
    },

    _hydrateWithCartSnapshot: function(data, keepalive) {
      var self = this;
      var base = data || {};
      return this._readCartSnapshot(keepalive).then(function(cart) {
        var snapshot = self._cartSnapshotData(cart || self._lastCartSnapshot);
        Object.keys(snapshot).forEach(function(k) { base[k] = snapshot[k]; });
        return base;
      });
    },

    _persistVidToCart: function(reason) {
      if (!this._vid || !this._rawFetch) return Promise.resolve(false);
      // Checkout always fires a keepalive write — even if another non-keepalive
      // sync is in flight, since navigation can cancel that request before the
      // attribute lands.
      var isCheckout = reason === 'checkout';
      if (!isCheckout) {
        if (this._cartVidSyncing) return Promise.resolve(false);
        try {
          if (sessionStorage.getItem('prive_brain_cart_vid') === this._vid) return Promise.resolve(false);
        } catch (_) {}
        this._cartVidSyncing = true;
      }
      var self = this;
      try {
        return this._rawFetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ attributes: { _prive_vid: this._vid } }),
          credentials: 'same-origin',
          keepalive: isCheckout
        }).then(function(resp) {
          if (resp && resp.ok) {
            try { sessionStorage.setItem('prive_brain_cart_vid', self._vid); } catch (_) {}
            return true;
          }
          return false;
        }).catch(function() { return false; }).then(function(ok) {
          if (!isCheckout) self._cartVidSyncing = false;
          return ok;
        });
      } catch (_) {
        if (!isCheckout) this._cartVidSyncing = false;
        return Promise.resolve(false);
      }
    },

    _wrapFetchForCart: function() {
      if (!window.fetch || window.__priveBrainFetchWrapped) return;
      window.__priveBrainFetchWrapped = true;
      var self = this;
      var origFetch = window.fetch.bind(window);
      window.fetch = function(input, init) {
        var url = '';
        try { url = typeof input === 'string' ? input : (input && input.url) || ''; } catch (_) {}
        var p = origFetch(input, init);
        if (!url || url.indexOf('/cart/') === -1) return p;
        return p.then(function(resp) {
          try {
            if (!resp || !resp.ok) return resp;
            var evt = null;
            if (url.indexOf('/cart/add') !== -1) evt = 'cart_add';
            else if (url.indexOf('/cart/change') !== -1) evt = 'cart_change';
            else if (url.indexOf('/cart/update') !== -1) evt = 'cart_update';
            else if (url.indexOf('/cart/clear') !== -1) evt = 'cart_clear';
            if (!evt) return resp;
            var data = {};
            try {
              var body = init && init.body;
              if (body && typeof body === 'string') {
                try {
                  var parsed = JSON.parse(body);
                  if (parsed && parsed.id) data.variant_id = String(parsed.id);
                  if (parsed && typeof parsed.quantity !== 'undefined') { var pq = parseInt(parsed.quantity, 10); if (!isNaN(pq)) data.quantity = pq; }
                } catch (_) {
                  try {
                    var fp = new URLSearchParams(body);
                    var id = fp.get('id'); if (id) data.variant_id = id;
                    var qty = fp.get('quantity'); if (qty !== null) { var uq = parseInt(qty, 10); if (!isNaN(uq)) data.quantity = uq; }
                  } catch (__) {}
                }
              } else if (body && typeof FormData !== 'undefined' && body instanceof FormData) {
                var fid = body.get('id'); if (fid) data.variant_id = String(fid);
                var fqty = body.get('quantity'); if (fqty !== null) { var fdq = parseInt(fqty, 10); if (!isNaN(fdq)) data.quantity = fdq; }
              }
            } catch (_) {}
            try { data.path = window.location.pathname; } catch (_) {}
            // Queue the base event synchronously so a navigation right after the
            // cart write (e.g. cartType="page" redirects to /cart) cannot drop it.
            var cached = self._cartSnapshotData(self._lastCartSnapshot);
            Object.keys(cached).forEach(function(k) { if (data[k] == null) data[k] = cached[k]; });
            self._track(evt, data);
            if (evt === 'cart_add' || evt === 'cart_change' || evt === 'cart_update') self._persistVidToCart(evt);
            // Enrich with a fresh /cart.js snapshot in the background.
            self._hydrateWithCartSnapshot({ path: data.path }, false).then(function(payload) {
              if (payload.cart_token || payload.cart_item_count || payload.cart_lines) self._track('cart_snapshot', payload);
            }).catch(function() {});
          } catch (_) {}
          return resp;
        }).catch(function(err) { throw err; });
      };
    },

    _trackCartView: function() {
      try {
        var path = window.location.pathname || '';
        if (path === '/cart' || path.indexOf('/cart') === 0) {
          var data = { path: path };
          var cached = this._cartSnapshotData(this._lastCartSnapshot);
          Object.keys(cached).forEach(function(k) { data[k] = cached[k]; });
          this._track('cart_view', data);
          var self = this;
          this._hydrateWithCartSnapshot({ path: path }, false).then(function(payload) {
            if (payload.cart_token || payload.cart_item_count || payload.cart_lines) self._track('cart_snapshot', payload);
          });
        }
      } catch (_) {}
    },

    _trackCheckoutStart: function() {
      var self = this;
      var fired = false;
      document.addEventListener('click', function(e) {
        if (fired) return;
        var el = e.target;
        if (!el || !el.closest) return;
        var hit = el.closest('a[href*="/checkout"], button[name="checkout"], input[name="checkout"], [data-prive-checkout]');
        if (!hit) return;
        fired = true;
        var data = {};
        try { data.path = window.location.pathname; } catch (_) {}
        try {
          if (hit.tagName === 'A' && hit.href) data.href = hit.href;
        } catch (_) {}
        var cached = self._cartSnapshotData(self._lastCartSnapshot);
        Object.keys(cached).forEach(function(k) { data[k] = cached[k]; });
        self._persistVidToCart('checkout');
        self._track('checkout_start', data);
        self._flush();
        if (!data.cart_token && !data.cart_lines) {
          self._hydrateWithCartSnapshot({ path: data.path, href: data.href }, true).then(function(payload) {
            if (payload.cart_token || payload.cart_item_count || payload.cart_lines) {
              self._track('checkout_cart_snapshot', payload);
              self._flush();
            }
          });
        }
      }, true);
      document.addEventListener('visibilitychange', function() {
        if (document.visibilityState === 'hidden') fired = false;
      });
    },

    _productJsonLd: function() {
      try {
        var scripts = document.querySelectorAll('script[type="application/ld+json"]');
        for (var i = 0; i < scripts.length; i++) {
          var obj = JSON.parse(scripts[i].textContent);
          if (obj && obj['@type'] === 'Product') return obj;
        }
      } catch (e) {}
      return null;
    },

    trackSimilarClick: function(handle, from) { this._track('similar_click', { handle: handle, from: from }); },
    trackWhatsAppTap: function(handle, title, vendor, price) {
      var data = { handle: handle };
      if (title) data.title = title;
      if (vendor) data.vendor = vendor;
      if (price != null) data.price = price;
      this._track('whatsapp_tap', data);
    },
    trackImageSearch: function(payload) {
      var data = payload && typeof payload === 'object' ? payload : {};
      this._track('image_search', data);
    },
    trackImageSearchSelect: function(payload) {
      var data = payload && typeof payload === 'object' ? payload : {};
      this._track('image_search_select', data);
    },
    linkCustomer: function(cid) { this._maybeIdentify(cid); },
    _maybeIdentify: function(cid) {
      if (!cid) return;
      var sid = String(cid);
      var stored = null;
      try { stored = localStorage.getItem('prive_brain_cid'); } catch(e) {}
      if (stored === sid) return;
      var sessionEmit = false;
      try { sessionEmit = sessionStorage.getItem('prive_brain_cid_emitted') === sid; } catch(e) {}
      if (sessionEmit) return;
      try { localStorage.setItem('prive_brain_cid', sid); } catch(e) {}
      try { sessionStorage.setItem('prive_brain_cid_emitted', sid); } catch(e) {}
      this._track('identify', { customerId: sid });
    }
  };

  BrainIntel.init();
  window.PriveBrainIntel = BrainIntel;

})();
